// Auth API - Better Auth returns raw responses, not wrapped in our standard format
import {
	API_BASE_URL,
	ApiError,
	GENERIC_ERROR_MESSAGE,
	request,
	uploadFile,
} from "./base";

/** Safely parse JSON from a response, throwing ApiError on failure */
async function safeJson(response: Response): Promise<unknown> {
	try {
		return await response.json();
	} catch (err) {
		console.error("Failed to parse response JSON:", err);
		throw new ApiError("PARSE_ERROR", GENERIC_ERROR_MESSAGE, response.status);
	}
}

// Auth Types
export type User = {
	id: string;
	name: string;
	email: string;
	emailVerified: boolean;
	image?: string;
	phoneNumber?: string;
	createdAt: string;
	updatedAt: string;
	termsAcceptedAt: string | null;
	termsVersion: string | null;
};

export type Session = {
	id: string;
	userId: string;
	token: string;
	expiresAt: string;
};

export const authApi = {
	async signUp(email: string, password: string, name: string, termsAccepted?: boolean) {
		const response = await fetch(`${API_BASE_URL}/api/auth/sign-up/email`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({
				email,
				password,
				name,
				terms_accepted: termsAccepted || false,
			}),
		});
		const data = (await safeJson(response)) as Record<string, string>;
		if (!response.ok) {
			throw new ApiError(
				data?.code || "AUTH_ERROR",
				data?.message || "Sign up failed",
				response.status,
			);
		}
		return data;
	},

	async signIn(email: string, password: string) {
		const response = await fetch(`${API_BASE_URL}/api/auth/sign-in/email`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ email, password }),
		});
		// Parse JSON inline with auth-specific fallback (safeJson uses
		// GENERIC_ERROR_MESSAGE which is too harsh for login failures)
		let data: Record<string, string> | null = null;
		try {
			data = (await response.json()) as Record<string, string>;
		} catch {
			// Non-JSON response — handled below
		}
		if (!response.ok) {
			throw new ApiError(
				data?.code || "AUTH_ERROR",
				data?.message || "Invalid email or password",
				response.status,
			);
		}
		return data as Record<string, string>;
	},

	async signOut() {
		const response = await fetch(`${API_BASE_URL}/api/auth/sign-out`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
		});
		return response.ok;
	},

	async getSession(): Promise<{ user: User; session: Session } | null> {
		const response = await fetch(`${API_BASE_URL}/api/auth/get-session`, {
			credentials: "include",
		});
		const data = (await safeJson(response)) as {
			user: User;
			session: Session;
		} | null;
		// Better Auth returns null when no session, or {user, session} when authenticated
		return data;
	},

	async forgotPassword(email: string) {
		const response = await fetch(`${API_BASE_URL}/api/auth/request-password-reset`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({
				email,
				redirectTo: `${window.location.origin}/reset-password`,
			}),
		});
		const data = (await safeJson(response)) as Record<string, string>;
		if (!response.ok) {
			throw new ApiError(
				data?.code || "AUTH_ERROR",
				data?.message || "Failed to send reset email",
				response.status,
			);
		}
		return data;
	},

	// Check if a phone number is registered
	async checkPhone(phoneNumber: string): Promise<{ exists: boolean }> {
		const response = await fetch(`${API_BASE_URL}/api/auth/check-phone`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ phoneNumber }),
		});
		const data = (await safeJson(response)) as { exists: boolean; error?: string };
		if (!response.ok) {
			throw new ApiError("CHECK_PHONE_ERROR", data?.error || GENERIC_ERROR_MESSAGE, response.status);
		}
		return data;
	},

	// Design tradeoff: This endpoint reveals whether an email is registered, which
	// improves UX (directs existing users to login) but enables email enumeration.
	// Acceptable for this B2B marketplace; rate-limit in production if needed.
	async checkEmail(
		email: string,
	): Promise<{ exists: boolean; verified: boolean; proName?: string }> {
		const response = await fetch(`${API_BASE_URL}/api/auth/check-email`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ email }),
		});
		if (!response.ok) {
			throw new ApiError("CHECK_EMAIL_ERROR", GENERIC_ERROR_MESSAGE, response.status);
		}
		const data = (await safeJson(response)) as {
			exists: boolean;
			verified: boolean;
			proName?: string;
		};
		return data;
	},

	async resendVerification(email: string) {
		// Omit credentials — after sign-up the user has an unverified session cookie.
		// Sending it causes Better Auth to return EMAIL_MISMATCH (400).
		const response = await fetch(
			`${API_BASE_URL}/api/auth/send-verification-email`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email,
					callbackURL: `${window.location.origin}/verify-email`,
				}),
			},
		);
		const data = (await safeJson(response)) as Record<string, string>;
		if (!response.ok) {
			throw new ApiError(
				data?.code || "AUTH_ERROR",
				data?.message || "Failed to send verification email",
				response.status,
			);
		}
		return data;
	},

	async resetPassword(token: string, newPassword: string) {
		const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ token, newPassword }),
		});
		const data = (await safeJson(response)) as Record<string, string>;
		if (!response.ok) {
			throw new ApiError(
				data?.code || "AUTH_ERROR",
				data?.message || "Failed to reset password",
				response.status,
			);
		}
		return data;
	},

	async getProfile() {
		return request<{
			id: string;
			name: string;
			email: string;
			phoneNumber: string | null;
			image: string | null;
			themePreference: string | null;
		}>("/api/user/me");
	},

	async updateProfile(data: { name?: string; phoneNumber?: string | null }) {
		return request<{
			id: string;
			name: string;
			email: string;
			phoneNumber: string | null;
			image: string | null;
		}>("/api/user/me", { method: "PUT", body: data });
	},

	async uploadAvatar(file: File) {
		return uploadFile<{ path: string; url: string }>(
			"/api/user/me/avatar",
			file,
		);
	},

	async removeAvatar() {
		return request<{ message: string }>("/api/user/me/avatar", {
			method: "DELETE",
		});
	},

	async updatePreferences(data: { themePreference: string }) {
		return request<{ themePreference: string | null }>(
			"/api/user/me/preferences",
			{ method: "PUT", body: data },
		);
	},

	async listAccounts(): Promise<{ providerId: string; accountId: string }[]> {
		const response = await fetch(
			`${API_BASE_URL}/api/auth/list-accounts`,
			{ credentials: "include" },
		);
		if (!response.ok) {
			throw new ApiError("AUTH_ERROR", "Failed to fetch account details", response.status);
		}
		const data = (await safeJson(response)) as { providerId: string; accountId: string }[] | null;
		return data || [];
	},

	async setPassword(newPassword: string) {
		return request<{ status: boolean }>("/api/user/me/set-password", {
			method: "POST",
			body: { newPassword },
		});
	},

	async changePassword(currentPassword: string, newPassword: string) {
		const response = await fetch(
			`${API_BASE_URL}/api/auth/change-password`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ currentPassword, newPassword }),
			},
		);
		const data = (await safeJson(response)) as Record<string, string>;
		if (!response.ok) {
			throw new ApiError(
				data?.code || "AUTH_ERROR",
				data?.message || "Failed to change password",
				response.status,
			);
		}
		return data;
	},

	async changeEmail(newEmail: string) {
		const response = await fetch(
			`${API_BASE_URL}/api/auth/change-email`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					newEmail,
					callbackURL: `${window.location.origin}/verify-email`,
				}),
			},
		);
		const data = (await safeJson(response)) as Record<string, unknown>;
		if (!response.ok) {
			throw new ApiError(
				(data?.code as string) || "AUTH_ERROR",
				(data?.message as string) || "Failed to change email",
				response.status,
			);
		}
		return data;
	},

	async sendPhoneChangeOtp(phoneNumber: string) {
		return request<{ message: string }>("/api/user/me/change-phone", {
			method: "POST",
			body: { phoneNumber },
		});
	},

	async verifyPhoneChange(phoneNumber: string, code: string) {
		return request<{
			id: string;
			name: string;
			email: string;
			phoneNumber: string | null;
			image: string | null;
		}>("/api/user/me/change-phone/verify", {
			method: "POST",
			body: { phoneNumber, code },
		});
	},

	async sendEmailChangeOtp(email: string) {
		return request<{ message: string }>("/api/user/me/change-email", {
			method: "POST",
			body: { email },
		});
	},

	async verifyEmailChange(email: string, code: string) {
		return request<{
			id: string;
			name: string;
			email: string;
			phoneNumber: string | null;
			image: string | null;
		}>("/api/user/me/change-email/verify", {
			method: "POST",
			body: { email, code },
		});
	},

	async acceptTerms(termsVersion: string) {
		return request<{ success: true }>("/api/auth/accept-terms", {
			method: "PATCH",
			body: { terms_version: termsVersion },
		});
	},
};
